// Timestamps from the API are ISO 8601 strings; the admin screens show them
// in the operator's local time, which is the machine running draft night.
export function formatTimestamp(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * A <input type="datetime-local"> value ("2026-08-24T19:00") is a local wall
 * clock with no offset in it, which ES2016+ requires to be read as local time.
 * Returns epoch ms so the caller can compare against serverNow() before
 * deciding to send anything; null for empty or unparseable.
 */
export function localInputToMs(value: string): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

/**
 * The inverse: an instant back into the input's local wall-clock format.
 * `new Date(ms).toISOString().slice(0, 16)` is the obvious wrong answer — it
 * renders UTC, and the API stores UTC (config.time_zone is commented out), so
 * a scheduled lottery would read back correct in London and five hours out in
 * Chicago. Component-by-component off the local getters is the only form that
 * round-trips.
 */
export function msToLocalInput(ms: number | null): string {
  if (ms == null) return ''
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

/**
 * "3d 4h", "2h 10m", "4:31", "0:07".
 *
 * Deliberately not formatClock (lib/draftView.ts): a lottery three days out
 * renders as "4320:00" through that one, which is a pick clock's format
 * applied to something that is not a pick clock.
 */
export function formatLeadTime(remainingMs: number | null): string {
  if (remainingMs == null) return '—'
  const total = Math.ceil(remainingMs / 1000)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const mins = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${pad2(mins)}m`
  return `${mins}:${pad2(secs)}`
}
